import { LoadingDots } from "PFComponents/loading_dots";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { PermissionRule } from "PFTypes";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router";

import useIsPermittedTo from "../../core/helpers/use_is_permitted_to";
import css from "./help_page.module.scss";

export const HelpPage = () => {
  const { t } = useTranslation("help");
  const location = useLocation();
  const navigate = useNavigate();
  const { data: currentProfile } = useCurrentProfile();
  const isPermittedTo = useIsPermittedTo(currentProfile);
  const baseDocUrl = window.PF.config.documentation_url || "";

  const [isLoading, setIsLoading] = useState(true);
  const [iframeUrl, setIframeUrl] = useState(baseDocUrl);
  const isNavigatingFromIframe = useRef(false);

  // Extract path after /help/ to use for deep linking
  useEffect(() => {
    // Don't update iframe URL if the navigation came from the iframe itself
    if (isNavigatingFromIframe.current) {
      isNavigatingFromIframe.current = false;
      return;
    }

    const deepLink = location.pathname.split("/help/")[1];

    if (deepLink) {
      setIframeUrl(baseDocUrl.endsWith("/") ? `${baseDocUrl}${deepLink}` : `${baseDocUrl}/${deepLink}`);
    }
  }, [location.pathname, baseDocUrl]);

  // Listen for iframe navigation and update the parent URL
  useEffect(() => {
    const handleIframeMessage = (event: MessageEvent) => {
      try {
        const allowedOrigin = new URL(baseDocUrl).origin;

        if (event.origin !== allowedOrigin) {
          return;
        }

        if (event.data && event.data.type === "IFRAME_NAVIGATE") {
          const iframePath = event.data.path.replace(baseDocUrl, "").replace(/^\//, "");
          const currentDeepLink = location.pathname.split("/help/")[1] || "";

          if (iframePath !== currentDeepLink) {
            isNavigatingFromIframe.current = true;
            navigate(`/help/${iframePath}`, { replace: true });
          }
        }
      } catch (e) {
        console.error("Error handling iframe message:", e);
      }
    };

    window.addEventListener("message", handleIframeMessage);
    return () => window.removeEventListener("message", handleIframeMessage);
  }, [baseDocUrl, location.pathname, navigate]);

  if (!isPermittedTo(PermissionRule.Documentation)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className={css.wrapper}>
      {isLoading && <LoadingDots circlesEnabled className={css.loader} />}
      <iframe
        src={iframeUrl}
        title={t("profindaDocumentation")}
        onLoad={() => setIsLoading(false)}
        className={css.iframe}
      />
    </div>
  );
};
